import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import "./ConversationFeature.css";

const VALID_CONVERSATIONS = [
  "scholarships",
  "saving",
  "plan",
  "expenses",
  "discounts",
  "money",
];

function PhoneStatusBar() {
  return (
    <>
      <img
        className="conversation-feature-phone__status-image"
        src="/status-bar-iphone.png"
        alt=""
        aria-hidden="true"
      />

      <span
        className="conversation-feature-phone__dynamic-island"
        aria-hidden="true"
      />
    </>
  );
}

function PhoneSideButtons() {
  return (
    <>
      <div
        className="conversation-feature-phone__buttons conversation-feature-phone__buttons--left"
        aria-hidden="true"
      >
        <i />
        <i />
        <i />
      </div>

      <div
        className="conversation-feature-phone__buttons conversation-feature-phone__buttons--right"
        aria-hidden="true"
      >
        <i />
      </div>
    </>
  );
}

function FeaturePhoneHeader({ assistantName, onlineLabel, backLabel }) {
  return (
    <header className="conversation-feature-phone__header">
      <button
        type="button"
        className="conversation-feature-phone__back"
        aria-label={backLabel}
        tabIndex={-1}
      >
        <FiChevronLeft />
      </button>

      <div className="conversation-feature-phone__avatar">ZY</div>

      <div className="conversation-feature-phone__identity">
        <strong>{assistantName}</strong>

        <span>
          <i />
          {onlineLabel}
        </span>
      </div>
    </header>
  );
}

function FeaturePhone({
  messages,
  buttonLabel,
  showConversation,
  phoneTranslations,
}) {
  return (
    <div className="conversation-feature-phone">
      <div className="conversation-feature-phone__frame">
        <PhoneSideButtons />

        <div className="conversation-feature-phone__screen">
          <PhoneStatusBar />

          <FeaturePhoneHeader
            assistantName={phoneTranslations.assistantName}
            onlineLabel={phoneTranslations.online}
            backLabel={phoneTranslations.backLabel}
          />

          <main className="conversation-feature-phone__chat">
            <div
              className={[
                "conversation-feature-phone__messages",
                showConversation ? "is-visible" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={[
                    "conversation-feature-message",
                    message.sender === "user"
                      ? "conversation-feature-message--user"
                      : "conversation-feature-message--assistant",
                  ].join(" ")}
                  style={{
                    "--message-delay": `${index * 240}ms`,
                  }}
                >
                  {message.content}
                </div>
              ))}

              {buttonLabel ? (
                <button
                  type="button"
                  className="conversation-feature-phone__action"
                  style={{
                    "--message-delay": `${messages.length * 240 + 100}ms`,
                  }}
                  tabIndex={-1}
                >
                  <span>{buttonLabel}</span>

                  <FiChevronRight />
                </button>
              ) : null}
            </div>
          </main>

          <img
            className="conversation-feature-phone__bottom-bar-image"
            src="/bottom-bar.png"
            alt=""
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

export default function ConversationFeature({
  eyebrow,

  title: titleOverride,
  description: descriptionOverride,

  conversation = "scholarships",

  messages: messagesOverride,
  buttonLabel: buttonLabelOverride,

  direction = "content-left",
  titlePalette = ["#d744b5", "#e96096", "#f28c43"],
}) {
  const { t } = useTranslation("home");

  const sectionRef = useRef(null);

  const [hasEntered, setHasEntered] = useState(false);

  const safeConversation = VALID_CONVERSATIONS.includes(conversation)
    ? conversation
    : "scholarships";

  const translatedConversation = useMemo(() => {
    const result = t(`conversationFeature.conversations.${safeConversation}`, {
      returnObjects: true,
    });

    if (!result || typeof result !== "object" || Array.isArray(result)) {
      return {
        title: {
          line1: "",
          line2: "",
        },
        description: "",
        messages: [],
        buttonLabel: "",
      };
    }

    return result;
  }, [t, safeConversation]);

  const phoneTranslations = useMemo(() => {
    const result = t("conversationFeature.phone", {
      returnObjects: true,
    });

    if (!result || typeof result !== "object" || Array.isArray(result)) {
      return {
        assistantName: "ZY",
        online: "",
        backLabel: "",
      };
    }

    return result;
  }, [t]);

  const translatedTitle =
    translatedConversation.title &&
    typeof translatedConversation.title === "object" &&
    !Array.isArray(translatedConversation.title)
      ? translatedConversation.title
      : {
          line1: "",
          line2: "",
        };

  const title =
    titleOverride ??
    (translatedTitle.line1 || translatedTitle.line2 ? (
      <>
        {translatedTitle.line1}

        {translatedTitle.line2 ? (
          <>
            <br />
            {translatedTitle.line2}
          </>
        ) : null}
      </>
    ) : null);

  const description =
    descriptionOverride ?? translatedConversation.description ?? "";

  const messages = Array.isArray(messagesOverride)
    ? messagesOverride
    : Array.isArray(translatedConversation.messages)
      ? translatedConversation.messages
      : [];

  const buttonLabel =
    buttonLabelOverride ?? translatedConversation.buttonLabel ?? "";

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          return;
        }

        setHasEntered(true);

        observer.disconnect();
      },
      {
        threshold: 0.35,
      },
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
    };
  }, []);

  const [
    titleStart,
    titleMiddle = titlePalette[0],
    titleEnd = titlePalette[1] ?? titlePalette[0],
  ] = titlePalette;

  return (
    <section
      ref={sectionRef}
      className={[
        "conversation-feature",
        `conversation-feature--${direction}`,
        hasEntered ? "is-visible" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        "--feature-title-start": titleStart,
        "--feature-title-middle": titleMiddle,
        "--feature-title-end": titleEnd,
      }}
    >
      <div className="conversation-feature__inner">
        <div className="conversation-feature__content">
          {eyebrow ? (
            <span className="conversation-feature__eyebrow">{eyebrow}</span>
          ) : null}

          {title ? <h2>{title}</h2> : null}

          {description ? <p>{description}</p> : null}
        </div>

        <div className="conversation-feature__visual">
          <FeaturePhone
            messages={messages}
            buttonLabel={buttonLabel}
            showConversation={hasEntered}
            phoneTranslations={phoneTranslations}
          />
        </div>
      </div>
    </section>
  );
}

export const CONVERSATION_TYPES = {
  scholarships: "scholarships",
  saving: "saving",
  plan: "plan",
  expenses: "expenses",
  discounts: "discounts",
  money: "money",
};
